/**
 * Represents a framework event with customizable details and behavior.
 *
 * The `FrameworkEvent` class implements the `IFrameworkEvent` interface and provides a way to create and manage events
 * with custom details and behavior. It allows you to:
 *
 * - Specify the event type and initial event details
 * - Access the event details
 * - Check if the event is cancelable and whether it has been canceled
 * - Control whether the event can bubble up the event hierarchy
 *
 * The `FrameworkEvent` class is designed to be used as a base class for creating custom event types that fit the needs
 * of your application or framework.
 *
 * @template TInit The type of the event details.
 * @template TType The type of the event type.
 */
export class FrameworkEvent {
    __type;
    #detail;
    #source;
    #canceled = false;
    #cancelable;
    #canBubble;
    #created = Date.now();
    /**
     * Creates a new framework event.
     *
     * @param __type - The event name used for listener matching.
     * @param args - Initialization options including detail payload, source, and flags.
     */
    constructor(__type, args) {
        this.__type = __type;
        this.#detail = args.detail;
        this.#source = args.source;
        this.#cancelable = !!args.cancelable;
        this.#canBubble = args.canBubble === undefined ? true : args.canBubble;
    }
    /**
     * Indicates whether the event can bubble up the event hierarchy.
     * The event will only bubble if this property is `true` and the event has not been canceled.
     * @returns {boolean} `true` if the event can bubble, `false` otherwise.
     */
    get bubbles() {
        return this.#canBubble && !this.#canceled;
    }
    /**
     * Gets the timestamp when the `FrameworkEvent` was created.
     * @returns {number} The timestamp of when the `FrameworkEvent` was created.
     */
    get created() {
        return this.#created;
    }
    /**
     * Indicates whether the event is cancelable.
     * If the event is cancelable, it can be prevented from occurring by calling the `preventDefault()` method.
     * @returns {boolean} `true` if the event is cancelable, `false` otherwise.
     */
    get cancelable() {
        return this.#cancelable;
    }
    /**
     * Indicates whether the event has been canceled.
     * If the event is cancelable and `preventDefault()` has been called, this property will be `true`.
     * @returns {boolean} `true` if the event has been canceled, `false` otherwise.
     */
    get canceled() {
        return this.#canceled;
    }
    /**
     * Gets the current event details.
     * @returns {FrameworkEventInitDetail<TInit>} The current event details.
     */
    get detail() {
        return this.#detail;
    }
    /**
     * Gets the source object that triggered the event.
     * @returns {FrameworkEventInitSource<TInit> | undefined} The source object that triggered the event, or `undefined` if the source is not available.
     */
    get source() {
        return this.#source;
    }
    /**
     * Gets the type of the `FrameworkEvent`.
     * @returns {TType} The type of the `FrameworkEvent`.
     */
    get type() {
        return this.__type;
    }
    /**
     * Prevents the default action of the event from occurring, if the event is cancelable.
     * If the event is cancelable, this method sets the `canceled` property to `true`.
     */
    preventDefault() {
        // Only cancelable events can be canceled
        if (this.cancelable) {
            this.#canceled = true;
        }
    }
    /**
     * Prevents the event from bubbling up the DOM tree, effectively stopping its propagation.
     * This method sets the `#canBubble` property to `false`, which indicates that the event should not be propagated further.
     */
    stopPropagation() {
        this.#canBubble = false;
    }
}
//# sourceMappingURL=FrameworkEvent.js.map